import i18n from '@/i18n';
import { imageSpaceAPI, imageStorageAPI, bumpImageSpaceUsageCounts, getImageSpaceUploadFolder } from '@/utils/api';
import {
  clearImageSpaceLookupPending,
  markImageSpaceLookupPending,
  rememberImageSpaceLookupMiss,
} from '@/utils/imageSpaceImportId';
import { pickUrlFromImageStorageResponse, resolveSignedDisplayUrl } from '@/utils/imageUrlUtils';
import { sanitizeManagedDisplayResult, warmDisplayCacheFromRecords } from '@/utils/imageDisplayAccess';
import { workbenchPreviewUrl } from '@/utils/workbenchImagePreview';
import { runWithImageSpaceIngestContextAsync } from '@/utils/imageSpaceIngestContext';
import { estimateDataUrlByteSize } from '@/utils/imageSpaceByteSize';

function pickPayloadDisplayUrl(payload) {
  if (!payload || typeof payload !== 'object') return '';
  return String(
    payload.viewUrl
    || payload.displayUrl
    || payload.urlDisplay
    || payload.storedViewUrl
    || payload.storedUrlDisplay
    || '',
  ).trim();
}

function rememberWorkflowDisplayUrl(url, viewUrl) {
  const canonical = String(url || '').trim();
  const display = String(viewUrl || '').trim();
  if (!canonical || !display) return;
  warmDisplayCacheFromRecords([{ url: canonical, viewUrl: display, displayUrl: display }]);
}

/** 把落库地址和可立即展示的 viewUrl 拼进步骤产出，避免缩略图先空白再等签图。 */
export function attachWorkflowImagePreview(saved, extraUrls = []) {
  const persisted = String(saved?.imageUrl || '').trim();
  const viewUrl = String(saved?.viewUrl || saved?.displayUrl || '').trim();
  const rest = (Array.isArray(extraUrls) ? extraUrls : [])
    .map((u) => String(u || '').trim())
    .filter((u) => u && u !== persisted && !u.startsWith('data:') && !u.startsWith('blob:'));
  return {
    spaceId: saved?.spaceId || '',
    imageUrl: persisted,
    viewUrl,
    displayUrl: viewUrl,
    outputImageUrlDisplay: viewUrl,
    compositeImageUrls: persisted ? [persisted, ...rest] : rest,
    compositeImageViewUrls: viewUrl ? [viewUrl, ...rest.map(() => '')] : [],
  };
}

async function resolveWorkflowSpaceDisplayUrl(rawUrl, options = {}) {
  const raw = String(rawUrl || '').trim();
  if (!raw) return '';
  const resolved = await resolveSignedDisplayUrl(raw, options);
  return sanitizeManagedDisplayResult(resolved, raw) || workbenchPreviewUrl(raw, options);
}

/**
 * 以图片空间单条记录为准解析展示 URL（与 AdminImageSpaceController 落库一致），
 * 避免仅用本地推断的 URL 与后台不一致导致预览仍显示主图。
 */
export async function refreshImageSpaceDisplayUrl(spaceId, fallbackUrl) {
  const id = String(spaceId || '').trim();
  const fb = String(fallbackUrl || '').trim();
  if (!id) return resolveWorkflowSpaceDisplayUrl(fb);
  try {
    const res = await imageSpaceAPI.getById(id);
    const inner = res?.image ?? res?.data ?? (res && res.id && !res.image ? res : null);
    const raw =
      inner && typeof inner === 'object'
        ? String(inner.url || inner.imageUrl || '').trim()
        : String(res?.url || '').trim();
    const signOptions = inner && typeof inner === 'object'
      ? {
        displayUrl: inner.displayUrl,
        viewUrl: inner.viewUrl,
        storedUrlDisplay: inner.storedUrlDisplay,
        storedViewUrl: inner.storedViewUrl,
      }
      : {
        displayUrl: res?.displayUrl,
        viewUrl: res?.viewUrl,
      };
    if (raw) {
      return resolveWorkflowSpaceDisplayUrl(raw, signOptions);
    }
  } catch {
    /* ignore */
  }
  return resolveWorkflowSpaceDisplayUrl(fb);
}

export async function toProcessableImageUrl(imageUrl) {
  if (!imageUrl || typeof imageUrl !== 'string') return '';
  if (imageUrl.startsWith('data:')) return imageUrl;
  if (imageUrl.startsWith('blob:')) {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (_) {
      return '';
    }
  }
  const fetchUrl = await resolveSignedDisplayUrl(imageUrl);
  let full = sanitizeManagedDisplayResult(fetchUrl, imageUrl) || workbenchPreviewUrl(imageUrl);
  if (!full) return '';
  if (full.startsWith('data:')) return full;
  if (!full.startsWith('http')) return full;
  try {
    const res = await fetch(full, { mode: 'cors' });
    if (!res.ok) return full;
    const blob = await res.blob();
    if (!blob.type || !blob.type.startsWith('image/')) return full;
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    return full;
  }
}

export async function saveWorkflowImageToSpace(prefix, imageUrl, name, options = {}) {
  const { navId, ingestKind = 'result', size, viewUrl: incomingViewUrl } = options;
  const id = `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  // 落盘完成前跳过对该 id 的 getById，避免并发预览/组包刷 404
  markImageSpaceLookupPending(id);
  let url = String(imageUrl || '').trim();
  if (!url) {
    clearImageSpaceLookupPending(id);
    throw new Error(i18n.t('workbench:ipeUtilError.emptyImageUrl'));
  }
  let savedViewUrl = String(incomingViewUrl || '').trim();
  try {
    if (url.startsWith('data:') || url.startsWith('blob:')) {
      const result = await imageStorageAPI.save(url, {
        folder: getImageSpaceUploadFolder(),
        registerImageSpace: false,
      });
      const serverPath = pickUrlFromImageStorageResponse(result);
      if (!serverPath) throw new Error(i18n.t('workbench:ipeUtilError.cannotWriteServerImage'));
      url = serverPath;
      savedViewUrl = pickPayloadDisplayUrl(result) || savedViewUrl;
    }

    let spaceId = '';
    await runWithImageSpaceIngestContextAsync(
      { navId, workflowStepNavId: navId, ingestKind },
      async () => {
        const payload = {
          id,
          url,
          imageUrl: url,
          originalUrl: url,
          name: name || id,
          ...(size > 0 ? { size } : {}),
          ...(savedViewUrl ? { viewUrl: savedViewUrl, displayUrl: savedViewUrl } : {}),
        };
        let res = await imageSpaceAPI.batchSave([payload]);
        let img = Array.isArray(res?.images) ? res.images[0] : null;
        if (!img || img.id == null || String(img.id).trim() === '') {
          res = await imageSpaceAPI.batchSave([payload], { dedupe: false });
          img = Array.isArray(res?.images) ? res.images[0] : null;
        }
        if (!img || img.id == null || String(img.id).trim() === '') {
          throw new Error(i18n.t('workbench:ipeUtilError.cannotFindUpstreamImage'));
        }
        spaceId = String(img.id).trim();
        const persisted = String(img.url || img.imageUrl || url).trim();
        if (persisted) url = persisted;
        savedViewUrl = pickPayloadDisplayUrl(img) || savedViewUrl;
      },
    );

    clearImageSpaceLookupPending(id);
    if (spaceId && spaceId !== id) clearImageSpaceLookupPending(spaceId);

    try {
      await bumpImageSpaceUsageCounts([spaceId]);
    } catch {
      /* ignore */
    }
    rememberWorkflowDisplayUrl(url, savedViewUrl);
    return { spaceId, imageUrl: url, viewUrl: savedViewUrl, displayUrl: savedViewUrl };
  } catch (err) {
    clearImageSpaceLookupPending(id);
    rememberImageSpaceLookupMiss(id);
    throw err;
  }
}

/**
 * 将裁剪/抠图/放大等步骤产出写入图片空间。
 * 先拉取为 data: 再经 signed-image 落盘为新的 /uploads 路径，避免与上游共用同一 URL
 * 导致对比视图缓存命中、前后两张图看起来完全一样。
 */
export async function persistProcessedImageToSpace(prefix, rawUrl, name, options = {}) {
  const { navId, ingestKind = 'result' } = options;
  const raw = String(rawUrl || '').trim();
  if (!raw) throw new Error(i18n.t('workbench:ipeUtilError.emptyImageUrl'));
  let dataUrl = raw.startsWith('data:') ? raw : await toProcessableImageUrl(raw);
  if (!dataUrl.startsWith('data:')) {
    dataUrl = await toProcessableImageUrl(dataUrl);
  }
  if (!dataUrl || !String(dataUrl).startsWith('data:')) {
    throw new Error(i18n.t('workbench:ipeUtilError.cannotConvertProcessResult'));
  }
  const byteSize = estimateDataUrlByteSize(dataUrl);
  const result = await imageStorageAPI.save(dataUrl, {
    folder: getImageSpaceUploadFolder(),
    registerImageSpace: false,
  });
  const canonical = pickUrlFromImageStorageResponse(result);
  if (!canonical) throw new Error(i18n.t('workbench:ipeUtilError.cannotWriteServerImage'));
  return saveWorkflowImageToSpace(prefix, canonical, name, {
    navId,
    ingestKind,
    size: byteSize,
    viewUrl: pickPayloadDisplayUrl(result),
  });
}

function persistedSpaceImage(res) {
  const img = Array.isArray(res?.images) ? res.images[0] : null;
  if (!img || typeof img !== 'object' || img.id == null || String(img.id).trim() === '') return null;
  return img;
}

export async function ensureInitialSpace(row) {
  const primaryUrl = String(row?.primaryImageUrl || '').trim();
  if (!primaryUrl) throw new Error(i18n.t('workbench:ipeUtilError.noMainImage'));
  const provisionalId = `src-img-${row.id}-${Date.now()}`;
  const canonical = primaryUrl;
  const payload = {
    id: provisionalId,
    url: canonical,
    imageUrl: canonical,
    originalUrl: primaryUrl,
    source: 'sourcing',
    name: (row.title && String(row.title).slice(0, 80)) || `找图选品主图-${row.id}`,
  };
  let res = await imageSpaceAPI.batchSave([payload]);
  let img = persistedSpaceImage(res);
  if (!img) {
    res = await imageSpaceAPI.batchSave([payload], { dedupe: false });
    img = persistedSpaceImage(res);
  }
  if (!img) throw new Error(i18n.t('workbench:ipeUtilError.cannotFindUpstreamImage'));
  const spaceId = String(img.id).trim();
  const imageUrl = String(img.url || img.imageUrl || canonical).trim() || canonical;
  try {
    await bumpImageSpaceUsageCounts([spaceId]);
  } catch {
    /* ignore */
  }
  return { spaceId, imageUrl };
}
